import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Group,
  Text,
  Box,
  createStyles,
  Popover,
  Image,
  MantineProvider,
  Button
} from "@mantine/core";
import tinycolor from "tinycolor2";

import FollowsBlock from "../../users/FollowsBlock";
import DefaultAvatar from "../../../src/images/fallback/default.png";

import FollowModel from "../../../models/Follow";
import DirectMessageModel from "../../../models/DirectMessage";

import { setTiptapSidemenu } from "../../../store/app";

const useStyles = createStyles((theme, { colors }) => ({
  container: { display: "block" },
  primaryButtonStyle: {
    backgroundColor: colors?.primary_button_background_color,
    color: colors?.primary_button_text_color,
    "&:hover": {
      backgroundColor: tinycolor(colors?.primary_button_background_color)
        .darken(4)
        .toString(),
    },
  },
}));

export default function FloatingProfileBlock({ children, user, space_slug }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentUser = useSelector((state) => state.user);
  const colors = useSelector((state) => state.colors);
  const { classes } = useStyles({ colors });

  const [visible, setVisible] = useState(false);
  const [followInfo, setFollowInfo] = useState(null);
  const [loadingFollowInfo, setLoadingFollowInfo] = useState(false);

  const hideTimeout = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (visible) {
      onFetchFollowInfo();
    }
  }, [visible]);

  const onFetchFollowInfo = () => {
    if (!space_slug || !user?.username) return;

    setLoadingFollowInfo(true);
    FollowModel.onFetchFollowInfo(
      { user_id: user.username, slug: space_slug },
      (data) => {
        setFollowInfo(data);
        setLoadingFollowInfo(false);
      },
      (data) => setLoadingFollowInfo(false)
    );
  };

  const handleMouseEnter = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
    if (handleMouseEnter.timeout) {
      clearTimeout(handleMouseEnter.timeout);
    }
    handleMouseEnter.timeout = setTimeout(() => {
      setVisible(true);
    }, 500);
  };

  const handleMouseLeave = (e) => {
    if (handleMouseEnter.timeout) {
      clearTimeout(handleMouseEnter.timeout);
    }
    if (boxRef.current && e.relatedTarget && e.relatedTarget.nodeType === Node.ELEMENT_NODE) {
      if (boxRef.current.contains(e.relatedTarget)) {
        return;
      }
    }
    
    hideTimeout.current = setTimeout(() => {
      setVisible(false);
    }, 150);
  };

  const onPressFollow = () => {
    if (!followInfo?.user_id) return;
    const action = followInfo.is_followed ? FollowModel.onUnfollow : FollowModel.onFollow;
    action(
      followInfo.user_id,
      (data) => {
        if (data.success) {
          onFetchFollowInfo();
        }
      },
      (err) => {}
    );
  };

  const onDirectMessage = () => {
    if (!followInfo?.user_id) return;
    
    DirectMessageModel.onFetchOrCreate(
      followInfo.user_id,
      (data) => {
        setVisible(false);
        dispatch(
          setTiptapSidemenu({
            opened: "direct",
            option: data.space.slug,
            allow_reflections: true
          })
        );
      },
      (err) => {}
    );
  };

  const onOpenProfile = () => {
    if (!user?.username) return;
    dispatch(setTiptapSidemenu({ opened: "profile", option: user.username }));
    setVisible(false);
  };

  if (!user || !currentUser?.authenticated) {
    return <>{children}</>;
  }

  return (
    <Box
      className={classes.container}
      ref={boxRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={e => e.stopPropagation()}
    >
      <Popover
        opened={visible}
        position="top"
        placement="start"
        radius="md"
        gutter={5}
        spacing={0}
        trapFocus={false}
        target={children}
        styles={{
          root: { display: "block" },
          popover: { backgroundColor: colors?.background_color }
        }}
      >
        <Group spacing={10} p={20}>
          <Image
            src={user.avatar?.original_url || user.avatar?.url || DefaultAvatar}
            radius={45}
            style={{ width: 90, height: 90, cursor: "pointer" }}
            onClick={onOpenProfile}
          />
          <Box>
            <Text
              order={3}
              mb={5}
              sx={{ fontWeight: 700, lineHeight: 1, cursor: "pointer", color: colors?.default_text_color }}
              onClick={onOpenProfile}
            >
              {user.full_name}
            </Text>

            <FollowsBlock
              user={user}
              colors={colors}
              followers_count={followInfo?.followers_count}
              followees_count={followInfo?.followees_count}
              prana_count={followInfo?.prana_count}
              posts_in_count={followInfo?.posts_in_count}
              likes_in_count={followInfo?.likes_in_count}
              top_parent_name={followInfo?.top_parent_name}
              onFetchFollowInfo={() => onFetchFollowInfo()}
            />

            <Group spacing={10}>
              {typeof followInfo?.is_followed === "boolean" && followInfo?.user_id !== currentUser?.id && (
                <MantineProvider theme={{ primaryShade: 6 }}>
                  <Button
                    className={classes.primaryButtonStyle}
                    radius="xl"
                    size="sm"
                    onClick={onPressFollow}
                  >
                    {followInfo.is_followed ? "Unfollow" : "Follow"}
                  </Button>
                </MantineProvider>
              )}
              {followInfo?.user_id !== currentUser?.id && (
                <MantineProvider theme={{ primaryShade: 6 }}>
                  <Button
                    className={classes.primaryButtonStyle}
                    radius="xl"
                    size="sm"
                    onClick={onDirectMessage}
                  >
                    {t("users.profileModal.message")}
                  </Button>
                </MantineProvider>
              )}
              <MantineProvider theme={{ primaryShade: 6 }}>
                <Button
                  className={classes.primaryButtonStyle}
                  radius="xl"
                  size="sm"
                  onClick={onOpenProfile}
                >
                  {t("users.profileModal.profile")}
                </Button>
              </MantineProvider>
            </Group>
          </Box>
        </Group>
      </Popover>
    </Box>
  );
}
